// Copyright (c) 2019 Deere & Company

import './index.scss';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Accordion from '../../../app/common/components/accordion';
import LabeledInput from '../../../shared/components/labeled-input';
import LabeledTextarea from '../../../shared/components/labeled-textarea';
import ClaimDetailsHeader from '../../../claim-details/components/claim-details-header';
import { getAccessPrivileges } from '../../../app/common/repositories';
import { updateNonDeereClaimData } from '../../../claims/repositories';

const formatDate = value => ( value ? moment( Number( value ) * 1000 ).utc().format( 'MM/DD/YYYY' ) : '' );
const formatDateWithTimeStamp = value => ( value ? moment( Number( value ) * 1000 ).utc().format( 'MM/DD/YYYY HH:mm:ss' ) : '' );
const toDateInputValue = value => ( value ? moment( Number( value ) * 1000 ).utc().format( 'YYYY-MM-DD' ) : '' );
const TODAY = moment().format( 'YYYY-MM-DD' );
const safeStr = value => ( value !== undefined && value !== null ? String( value ) : '' );
const NON_DEERE_EDIT_GROUP = 'NDCLWWSA';
const NON_NEGATIVE_DECIMAL = /^\d*\.?\d*$/;
const NUMERIC_DECIMAL_FIELDS = [
    'machineWorkingHours', 'totalLabourTime', 'labourCost', 'partCost', 'miscCost', 'totalCostAccepted',
];

// ─── Claas ────────────────────────────────────────────────────────────────────
const CLAAS_EDITABLE_FIELDS = [
    'claimNumber', 'claimType', 'countryCode', 'vehicleSerialNum', 'engineSerialNum',
    'deliveryDate', 'failureDate', 'processingDate', 'partNumber', 'partComplaint',
    'machineWorkingHours', 'totalLabourTime', 'labourCost',
    'partCost', 'miscCost', 'totalCostAccepted', 'updatedTs',
];

const CLAAS_ROW_GROUPS = [
    [
        { label: 'Claim Number',            field: 'claimNumber' },
        { label: 'Claim Type',              field: 'claimType' },
        { label: 'Country Code',            field: 'countryCode' },
        { label: 'Vehicle Serial Number',   field: 'vehicleSerialNum' },
    ],
    [
        { label: 'Engine Serial Number',    field: 'engineSerialNum' },
        { label: 'Delivery Date',           field: 'deliveryDate',    isDate: true },
        { label: 'Failure Date',            field: 'failureDate',     isDate: true },
        { label: 'Processing Date',         field: 'processingDate',  isDate: true },
    ],
    [
        { label: 'Part Number',             field: 'partNumber' },
        { label: 'Part Description',        field: 'partDescription' },
        { label: 'Machine Working Hours',   field: 'machineWorkingHours' },
         { label: 'Total Labour Time',       field: 'totalLabourTime' },
    ],
    [
        { label: 'Labour Cost (€)',            field: 'labourCost' },
        { label: 'Labour Cost ($)',          field: 'labourCostUSD' },
        { label: 'Part Cost (€)',               field: 'partCost' },
         { label: 'Part Cost ($)',            field: 'partCostUSD' },
    ],
    [
        { label: 'Misc Cost (€)',               field: 'miscCost' },
        { label: 'Misc Cost ($)',            field: 'miscCostUSD' },
        { label: 'Total Cost Accepted (€)',     field: 'totalCostAccepted' },
        { label: 'Total Cost Accepted ($)',   field: 'totalCostAcceptedUSD' },
    ],
    [
        { label: 'Uploaded By',             field: 'uploadBy' },
        { label: 'Updated By',              field: 'updatedBy' },
        { label: 'Part Complaint',          field: 'partComplaint', isTextarea: true },
    ],
];

// ─── Liebherr ─────────────────────────────────────────────────────────────────
// TODO: Update LIEBHERR_ROW_GROUPS and LIEBHERR_EDITABLE_FIELDS with Liebherr-specific fields
const LIEBHERR_EDITABLE_FIELDS = [ ...CLAAS_EDITABLE_FIELDS ];

const LIEBHERR_ROW_GROUPS = [ ...CLAAS_ROW_GROUPS ];

// ─── Wirtgen ──────────────────────────────────────────────────────────────────
// TODO: Update WIRTGEN_ROW_GROUPS and WIRTGEN_EDITABLE_FIELDS with Wirtgen-specific fields
const WIRTGEN_EDITABLE_FIELDS = [ ...CLAAS_EDITABLE_FIELDS ];

const WIRTGEN_ROW_GROUPS = [ ...CLAAS_ROW_GROUPS ];

// ─── Factory lookup ───────────────────────────────────────────────────────────
const FACTORY_CONFIG = {
    claas:    { rowGroups: CLAAS_ROW_GROUPS,    editableFields: CLAAS_EDITABLE_FIELDS },
    liebherr: { rowGroups: LIEBHERR_ROW_GROUPS, editableFields: LIEBHERR_EDITABLE_FIELDS },
    wirtgen:  { rowGroups: WIRTGEN_ROW_GROUPS,  editableFields: WIRTGEN_EDITABLE_FIELDS },
};

const getFactoryConfig = factoryCode => FACTORY_CONFIG[( factoryCode || '' ).toLowerCase()] || FACTORY_CONFIG.claas;

class NonDeereClaimDetails extends Component {

    constructor ( props ) {

        super( props );
        this.state = {
            isEdit: false,
            isSaving: false,
            saveError: null,
            canEdit: false,
            editedData: { ...props.claimData },
        };
        this.sectionRef = React.createRef();

    }

    componentDidMount () {

        getAccessPrivileges().then( response => {

            const userGroupList = ( response && response.userGroupList ) || [];
            this.setState( { canEdit: userGroupList.includes( NON_DEERE_EDIT_GROUP ) } );

        } ).catch( () => {

            this.setState( { canEdit: false } );

        } );

    }

    componentWillReceiveProps ( nextProps ) {

        if ( nextProps.claimData !== this.props.claimData ) {

            this.setState( {
                isEdit: false,
                saveError: null,
                editedData: { ...nextProps.claimData },
            } );

        }

    }

    componentDidUpdate ( prevProps, prevState ) {

        const dataChanged = prevState.editedData !== this.state.editedData;
        const editModeOpened = !prevState.isEdit && this.state.isEdit;

        if ( ( dataChanged || editModeOpened ) && this.sectionRef.current ) {

            this.sectionRef.current.querySelectorAll( 'textarea' ).forEach( ta => {

                ta.style.height = 'auto';
                ta.style.height = `${ta.scrollHeight}px`;

            } );

        }

    }

    onEditClick = () => {

        this.setState( { isEdit: true } );

    }

    onCancelClick = () => {

        this.setState( {
            isEdit: false,
            saveError: null,
            editedData: { ...this.props.claimData },
        } );

    }

    onSaveClick = () => {

        const { factoryCode } = this.props;
        const { editedData } = this.state;

        const requiredFields = [
            { field: 'claimNumber',       label: 'Claim Number' },
            { field: 'vehicleSerialNum',  label: 'Vehicle Serial Number' },
            { field: 'engineSerialNum',   label: 'Engine Serial Number' },
            { field: 'partNumber',        label: 'Part Number' },
        ];

        const emptyFields = requiredFields
            .filter( ( { field } ) => !editedData[field] || String( editedData[field] ).trim() === '' )
            .map( ( { label } ) => label );

        if ( emptyFields.length > 0 ) {
            this.setState( { saveError: `Note: ${emptyFields.join( ', ' )} cannot be empty.` } );
            return;
        }

        this.setState( { isSaving: true, saveError: null } );

        updateNonDeereClaimData( factoryCode, editedData )
            .then( updatedClaim => {

                this.setState( {
                    isEdit: false,
                    isSaving: false,
                    editedData: updatedClaim || editedData,
                } );

            } )
            .catch( err => {

                this.setState( {
                    isSaving: false,
                    saveError: err && err.message ? err.message : 'Failed to save. Please try again.',
                } );

            } );

    }

    onDateFieldChange = ( field, dateString ) => {

        const unixSeconds = dateString ? moment.utc( dateString ).unix() : '';
        this.onFieldChange( field, unixSeconds );

    }

    onFieldChange = ( field, value ) => {

        this.setState( prevState => ( {
            editedData: {
                ...prevState.editedData,
                [field]: value,
            },
        } ) );

    }

    onNumericFieldChange = ( field, value ) => {

        if ( NON_NEGATIVE_DECIMAL.test( value ) ) this.onFieldChange( field, value );

    }

    getDisplayValue = ( field, isDate ) => {

        const raw = this.state.editedData[field];
        if ( isDate ) return formatDate( raw );
        return safeStr( raw );

    }

    render () {

        const {
            claimDetailsInfo,
            onBackToResult,
            getPreviousClaimDetails,
            getNextClaimDetails,
            factoryCode,
        } = this.props;

        const { isEdit, isSaving, saveError, editedData, canEdit } = this.state;
        const { rowGroups, editableFields } = getFactoryConfig( factoryCode );

        return (
            <div>

                {/* ── Exact same header as standard claim details ── */}
                <ClaimDetailsHeader
                    selectedClaimNumber={String( editedData.claimNumber || '' )}
                    claimDetailsInfo={claimDetailsInfo}
                    onBackToResult={onBackToResult}
                    getPreviousClaimDetails={getPreviousClaimDetails}
                    getNextClaimDetails={getNextClaimDetails}
                    handlePrintClick={() => window.print()}
                    callFrom="findClaim"
                />

                <div className="claim-sections">
                    <section>

                        {/* ── Sub-header info row ── */}
                        <section className="claim-header-section">
                            <div>
                                <label>Factory Code:</label>
                                <span>{ factoryCode ? factoryCode.charAt( 0 ).toUpperCase() + factoryCode.slice( 1 ) : '' }</span>
                            </div>
                            <div>
                                <label>Country Code:</label>
                                <span>{ editedData.countryCode || '' }</span>
                            </div>
                            <div>
                                <label>Processing Date:</label>
                                <span>{ formatDate( editedData.processingDate ) }</span>
                            </div>
                            <div>
                                <label>Updated By:</label>
                                <span>{ editedData.updatedBy || '' }</span>
                            </div>
                            <div>
                                <label>Load Timestamp:</label>
                                <span>{ formatDateWithTimeStamp( editedData.wrntyLoadTs ) }</span>
                            </div>
                        </section>

                        {/* ── Claim Details Accordion ── */}
                        <Accordion
                            accordionId="non-deere-claim-details-accordion"
                            title="Claim Details"
                            isExpanded={true}
                        >
                            <div className="basic-claim">
                                <section className="claim-information" ref={this.sectionRef}>

                                    {/* Edit / Save / Cancel icons — top-right of section */}
                                    <header>
                                        <h4 className="page-header">
                                            <i
                                                title="Edit"
                                                className={`fa fa-edit ${ canEdit && !isEdit ? 'show' : 'hide' }`}
                                                onClick={canEdit ? this.onEditClick : undefined}
                                            />
                                            <i
                                                title="Save"
                                                className={`fa fa-save ${ canEdit && isEdit && !isSaving ? 'show' : 'hide' }`}
                                                onClick={this.onSaveClick}
                                            />
                                            <i
                                                title="Cancel"
                                                className={`fa fa-remove ${ canEdit && isEdit && !isSaving ? 'show' : 'hide' }`}
                                                onClick={this.onCancelClick}
                                            />
                                            { isSaving && <span className="non-deere-saving-indicator"> Saving...</span> }
                                        </h4>
                                        { saveError && <div className="non-deere-save-error">{ saveError }</div> }
                                    </header>

                                    {/* Field rows — 4 columns each */}
                                    { rowGroups.map( ( row, rowIdx ) => (
                                        <div key={rowIdx} className="rows">
                                            { row.map( ( { label, field, isDate, isTextarea } ) => {

                                                const isEditable = isEdit && editableFields.includes( field );
                                                return (
                                                    <div key={field} className="four-cols">
                                                        { isDate && isEditable ? (
                                                            <LabeledInput
                                                                className="basic-claim-info"
                                                                name={field}
                                                                isEditable={true}
                                                                label={label}
                                                                type="date"
                                                                max={TODAY}
                                                                value={toDateInputValue( this.state.editedData[field] )}
                                                                onChange={e => this.onDateFieldChange( field, e.target.value )}
                                                            />
                                                        ) : isTextarea ? (
                                                            <LabeledTextarea
                                                                className="basic-claim-info"
                                                                name={field}
                                                                isEditable={isEditable}
                                                                label={label}
                                                                value={this.getDisplayValue( field, isDate )}
                                                                onChange={e => this.onFieldChange( field, e.target.value )}
                                                                onInput={e => { e.target.style.height = 'auto'; e.target.style.height = `${e.target.scrollHeight}px`; }}
                                                                onFocus={e => { const len = e.target.value.length; e.target.setSelectionRange( len, len ); }}
                                                                style={{ resize: 'none', overflow: 'hidden', width: '100%' }}
                                                            />
                                                        ) : (
                                                            <LabeledInput
                                                                className="basic-claim-info"
                                                                name={field}
                                                                isEditable={isEditable}
                                                                label={label}
                                                                type={NUMERIC_DECIMAL_FIELDS.includes( field ) ? 'number' : 'text'}
                                                                inputMode={NUMERIC_DECIMAL_FIELDS.includes( field ) ? 'decimal' : undefined}
                                                                min={NUMERIC_DECIMAL_FIELDS.includes( field ) ? '0' : undefined}
                                                                step={NUMERIC_DECIMAL_FIELDS.includes( field ) ? 'any' : undefined}
                                                                value={this.getDisplayValue( field, isDate )}
                                                                onChange={e => ( NUMERIC_DECIMAL_FIELDS.includes( field ) ?
                                                                    this.onNumericFieldChange( field, e.target.value ) :
                                                                    this.onFieldChange( field, e.target.value ) )}
                                                            />
                                                        ) }
                                                    </div>
                                                );

                                            } ) }
                                        </div>
                                    ) ) }

                                </section>
                            </div>
                        </Accordion>

                    </section>
                </div>

            </div>
        );

    }

}

NonDeereClaimDetails.displayName = 'NonDeereClaimDetails';
NonDeereClaimDetails.propTypes = {
    claimData: PropTypes.object.isRequired,
    claimDetailsInfo: PropTypes.object.isRequired,
    factoryCode: PropTypes.string.isRequired,
    onBackToResult: PropTypes.func.isRequired,
    getPreviousClaimDetails: PropTypes.func.isRequired,
    getNextClaimDetails: PropTypes.func.isRequired,
};

export default NonDeereClaimDetails;
